import moment from "moment";

export const fillMissingDates = (data, startDate, endDate, dateField = "date2", valueField = "value", typeField = "type") => {
  if (!data?.length) {
    return [];
  }

  const start = moment(startDate);
  const end = moment(endDate);
  
  if (!start.isValid() || !end.isValid() || start.isAfter(end)) {
    return data;
  }

  const typesSet = new Set();
  const existingDataMap = new Map();
  
  data.forEach(item => {
    const type = item[typeField];
    typesSet.add(type);
    
    const itemDate = moment(item[dateField]);
    if (itemDate.isValid()) {
      const normalizedDate = itemDate.format("YYYY/MM/DD");
      const key = `${normalizedDate}_${type}`;
      existingDataMap.set(key, item);
    }
  });

  const types = Array.from(typesSet);
  const filledData = [];
  
  for (let date = start.clone(); date.isSameOrBefore(end); date.add(1, 'day')) {
    const dateStr = date.format("YYYY/MM/DD");
    
    for (const type of types) {
      const key = `${dateStr}_${type}`;
      const existingItem = existingDataMap.get(key);
      
      if (existingItem) {
        filledData.push(existingItem);
      } else {
        filledData.push({
          [dateField]: dateStr,
          [typeField]: type,
          [valueField]: 0
        });
      }
    }
  }

  return filledData;
};

export const processChartData = (sourceData, viewTypeFilter, dateFormat = "DD/MM/YYYY", startDate = null, endDate = null) => {
  if (!sourceData?.length) return sourceData || [];

  if (viewTypeFilter === "Daily") {
    if (startDate && endDate) {
      return fillMissingDates(sourceData, startDate, endDate);
    }
    return sourceData;
  }

  if (viewTypeFilter === "Weekly") {
    const weeklyData = new Map();
    
    sourceData.forEach((item) => {
      const itemDate = moment(item.date2, dateFormat === "DD/MM/YYYY" ? "YYYY/MM/DD" : "YYYY-MM-DD");
      const weekStart = itemDate.startOf('week');
      const weekEnd = itemDate.clone().endOf('week');
      const weekLabel = `${weekStart.format('MMM DD')} - ${weekEnd.format('MMM DD')} ${weekStart.year()}`;
      const weekKey = `${weekStart.format('YYYY/MM/DD')}_${item.type}`;
      
      if (!weeklyData.has(weekKey)) {
        weeklyData.set(weekKey, {
          date2: weekLabel,
          type: item.type,
          value: 0,
          sortDate: weekStart.format('YYYY/MM/DD')
        });
      }
      weeklyData.get(weekKey).value += item.value;
    });
    
    return Array.from(weeklyData.values()).sort((a, b) => 
      moment(a.sortDate, 'YYYY/MM/DD').diff(moment(b.sortDate, 'YYYY/MM/DD'))
    );
  }

  if (viewTypeFilter === "Monthly") {
    const monthlyData = new Map();
    
    sourceData.forEach((item) => {
      const itemDate = moment(item.date2, dateFormat === "DD/MM/YYYY" ? "YYYY/MM/DD" : "YYYY-MM-DD");
      const monthStart = itemDate.startOf('month');
      const monthLabel = monthStart.format('MMM YYYY');
      const monthKey = `${monthStart.format('YYYY/MM')}_${item.type}`;
      
      if (!monthlyData.has(monthKey)) {
        monthlyData.set(monthKey, {
          date2: monthLabel,
          type: item.type,
          value: 0,
          sortDate: monthStart.format('YYYY/MM/DD')
        });
      }
      monthlyData.get(monthKey).value += item.value;
    });
    
    return Array.from(monthlyData.values()).sort((a, b) => 
      moment(a.sortDate, 'YYYY/MM/DD').diff(moment(b.sortDate, 'YYYY/MM/DD'))
    );
  }

  return sourceData;
};

export const getPresetDateRange = (preset) => {
  const today = moment();
  switch (preset) {
    case "last3Days":
      return {
        start: today.clone().subtract(2, 'days'),
        end: today.clone()
      };
    case "lastWeek":
      return {
        start: today.clone().subtract(1, 'week').startOf('week'),
        end: today.clone().subtract(1, 'week').endOf('week')
      };
    case "lastMonth":
      return {
        start: today.clone().subtract(1, 'month').startOf('month'),
        end: today.clone().subtract(1, 'month').endOf('month')
      };
    case "lastQuarter":
      return {
        start: today.clone().subtract(1, 'quarter').startOf('quarter'),
        end: today.clone().subtract(1, 'quarter').endOf('quarter')
      };
    default:
      return null;
  }
};

export const isWeekend = (date, dateFormat = "YYYY-MM-DD") => {
  const momentDate = moment(date, dateFormat);
  const dayOfWeek = momentDate.day(); 
  return dayOfWeek === 0 || dayOfWeek === 6;
};


export const addWeekendAnnotations = (baseConfig, chartData, dateField = "date2", viewType = "Daily") => {
  if (!chartData?.length || viewType !== "Daily") {
    return baseConfig;
  }

  const uniqueDates = [...new Set(chartData.map(item => item[dateField]))];
  const weekendDates = new Set();
  
  uniqueDates.forEach((date) => {
    const momentDate = moment(date, "YYYY-MM-DD");
    const dayOfWeek = momentDate.day();
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      weekendDates.add(date);
    }
  });

  return {
    ...baseConfig,
    xAxis: {
      ...baseConfig.xAxis,
      label: {
        ...baseConfig.xAxis?.label,
        formatter: (text) => {
          const isWeekend = weekendDates.has(text);
          if (isWeekend) {
            const momentDate = moment(text, "YYYY-MM-DD");
            const dayOfWeek = momentDate.day();
            const dayLabel = dayOfWeek === 0 ? 'Sunday' : 'Saturday';
            return `${text}\n${dayLabel}`;
          }
          return text;
        },
        style: {
          fill: (text) => {
            const isWeekend = weekendDates.has(text);
            return isWeekend ? '#ff4d4f' : '#000';
          },
          fontSize: 30,
          fontWeight: (text) => {
            const isWeekend = weekendDates.has(text);
            return isWeekend ? 'bold' : 'normal';
          }
        }
      }
    }
  };
};

export const getDataForPeriod = (data, startDate, endDate, dateField = "date2") => {
  if (!data?.length) return [];
  
  const start = moment(startDate);
  const end = moment(endDate);
  
  return data.filter(item => {
    const itemDate = moment(item[dateField], "YYYY/MM/DD");
    return itemDate.isBetween(start, end, 'day', '[]');
  });
};

export const aggregateDataByType = (data, valueField = "value", typeField = "type") => {
  if (!data?.length) return {};
  
  const typeStats = {};
  data.forEach(item => {
    const type = item[typeField];
    if (!typeStats[type]) {
      typeStats[type] = 0;
    }
    typeStats[type] += item[valueField] || 0;
  });
  
  return typeStats;
};

export const getWeekBoundaries = (date) => {
  const momentDate = moment(date);
  return {
    start: momentDate.clone().startOf('week'),
    end: momentDate.clone().endOf('week')
  };
};

export const getMonthBoundaries = (date) => {
  const momentDate = moment(date);
  return {
    start: momentDate.clone().startOf('month'),
    end: momentDate.clone().endOf('month')
  };
};
